import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Popper,
  Skeleton,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TargetIcon from "@mui/icons-material/GpsFixed";
import React from "react";

// Utility functions for difficulty color coding
const getDifficultyColor = (difficulty: number): { color: string; bgColor: string; label: string } => {
  if (difficulty <= 30) {
    return { color: "#16a34a", bgColor: "#dcfce7", label: "Easy" };
  } else if (difficulty <= 60) {
    return { color: "#ea580c", bgColor: "#fed7aa", label: "Medium" };
  } else {
    return { color: "#dc2626", bgColor: "#fecaca", label: "Hard" };
  }
};

const getVolumeColor = (volume: number): string => {
  if (volume >= 1000) return "#5048E5";
  if (volume >= 100) return "#7c3aed";
  return "#6b7280";
};

const SuggestionsContainer = styled(Paper)<{ customWidth?: string | number }>(({ theme, customWidth }) => ({
  width: customWidth || "505px", // Default 505px or custom width
  maxHeight: 400,
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
  borderRadius: 12,
  background: "linear-gradient(180deg, #ffffff 0%, #fefefe 100%)",
}));

const SuggestionItem = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 60,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(80, 72, 229, 0.08)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(80, 72, 229, 0.15)",
    borderRadius: 8,
    margin: "0 8px",
    padding: theme.spacing(2, 2),
  },
  "&:active": {
    transform: "translateY(0px)",
    boxShadow: "0 2px 8px rgba(80, 72, 229, 0.2)",
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

// New styled components for table-like layout
const KeywordColumn = styled(Box)({
  flex: 1,
  textAlign: "left",
  paddingRight: 16,
});

const DifficultyColumn = styled(Box)({
  width: 80,
  textAlign: "center",
  paddingRight: 8,
});

const VolumeColumn = styled(Box)({
  width: 80,
  textAlign: "right",
  fontWeight: 600,
});


// Header row component
const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2.5),
  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px 12px 0 0",
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.8px",
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

// New styled components for enhanced UI
const DifficultyBadge = styled(Chip)<{ difficulty: number }>(({ difficulty }) => {
  const colors = getDifficultyColor(difficulty);
  return {
    backgroundColor: colors.bgColor,
    color: colors.color,
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 24,
    borderRadius: 12,
    border: `1px solid ${colors.color}20`,
    "& .MuiChip-label": {
      paddingLeft: 8,
      paddingRight: 8,
    },
    "& .MuiChip-icon": {
      color: colors.color,
      fontSize: 14,
    },
  };
});

const VolumeBadge = styled(Box)<{ volume: number }>(({ volume }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  backgroundColor: `${getVolumeColor(volume)}10`,
  color: getVolumeColor(volume),
  padding: "4px 10px",
  borderRadius: 16,
  fontSize: "0.8rem",
  fontWeight: 600,
  border: `1px solid ${getVolumeColor(volume)}20`,
}));

const KeywordText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: "#1e293b",
  fontSize: "0.9rem",
  lineHeight: 1.4,
  transition: "color 0.2s ease",
}));

interface KeywordSuggestion {
  keyword: string;
  searchVolume: number;
  keywordDifficulty: number;
  cpc: number;
  competition: number;
}

interface KeywordSuggestionsDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  suggestions: KeywordSuggestion[];
  loading: boolean;
  error: string | null;
  onSuggestionSelect: (keyword: string) => void;
  onClose: () => void;
  dropdownRef?: React.RefObject<HTMLDivElement>;
  width?: string | number;
}

const formatNumber = (num: number | null): string => {
  // Handle null values for when search volume is not available
  if (num === null || num === undefined) {
    return "N/A";
  }
  // Return raw numbers with comma separators instead of K/M format
  return num.toLocaleString();
};


const SkeletonItem: React.FC = () => (
  <ListItem sx={{ py: 1.5, px: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <KeywordColumn>
      <Skeleton variant="text" width="70%" height={20} />
    </KeywordColumn>
    <DifficultyColumn>
      <Skeleton variant="text" width={30} height={20} />
    </DifficultyColumn>
    <VolumeColumn>
      <Skeleton variant="text" width={50} height={20} />
    </VolumeColumn>
  </ListItem>
);

const KeywordSuggestionsDropdown: React.FC<KeywordSuggestionsDropdownProps> = ({
  anchorEl,
  open,
  suggestions,
  loading,
  error,
  onSuggestionSelect,
  onClose,
  dropdownRef,
  width,
}) => {
  const handleSuggestionClick = (keyword: string) => {
    onSuggestionSelect(keyword);
    // Don't call onClose() here - let the parent component handle closing
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
      modifiers={[
        {
          name: "flip",
          enabled: true,
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            altAxis: true,
            tether: false,
            padding: 16,
          },
        },
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
      ]}
    >
      <SuggestionsContainer ref={dropdownRef} customWidth={width}>
        {loading && (
          <Box>
            <LoadingContainer sx={{ 
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: "12px 12px 0 0"
            }}>
              <CircularProgress 
                size={24} 
                sx={{ 
                  color: "#5048E5",
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#475569",
                  fontWeight: 500,
                  fontSize: "0.9rem"
                }}
              >
                Finding keyword suggestions...
              </Typography>
            </LoadingContainer>
            <Divider />
            <List sx={{ py: 0 }}>
              {[1, 2, 3, 4].map((index) => (
                <SkeletonItem key={index} />
              ))}
            </List>
          </Box>
        )}

        {!loading && error && (
          <EmptyState>
            <Box sx={{ 
              backgroundColor: '#fef2f2', 
              borderRadius: 2, 
              p: 3, 
              border: '1px solid #fecaca' 
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  color: '#dc2626',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                ⚠️ Failed to load suggestions
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {error}
              </Typography>
            </Box>
          </EmptyState>
        )}

        {!loading && !error && suggestions.length === 0 && (
          <EmptyState>
            <Box sx={{ 
              backgroundColor: '#f0f9ff', 
              borderRadius: 2, 
              p: 4, 
              border: '1px solid #bae6fd' 
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1.5, 
                  color: '#0284c7',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'center'
                }}
              >
                💡 No suggestions found
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                Try a different keyword or check your spelling
              </Typography>
            </Box>
          </EmptyState>
        )}

        {!loading && !error && suggestions.length > 0 && (
          <Box>
            {/* Header */}
            <HeaderRow>
              <KeywordColumn>
                <Typography variant="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  💡 Keyword ({suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''})
                </Typography>
              </KeywordColumn>
              <DifficultyColumn>
                <Typography variant="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TargetIcon sx={{ fontSize: 12 }} />
                  KD
                </Typography>
              </DifficultyColumn>
              <VolumeColumn>
                <Typography variant="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 12 }} />
                  SV
                </Typography>
              </VolumeColumn>
            </HeaderRow>
            
            {/* Suggestions List */}
            <List sx={{ py: 0, maxHeight: 320, overflow: "auto" }}>
              {suggestions.map((suggestion, index) => {
                const isFirstItem = index === 0;
                const hasNoData = suggestion.searchVolume === null || suggestion.keywordDifficulty === null;
                
                return (
                  <SuggestionItem
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion.keyword)}
                    sx={{
                      // Add visual distinction for first item (typed keyword)
                      ...(isFirstItem && {
                        backgroundColor: 'rgba(80, 72, 229, 0.04)',
                        border: '1px solid rgba(80, 72, 229, 0.2)',
                        borderRadius: 1,
                        margin: '0 8px',
                        '&:hover': {
                          backgroundColor: 'rgba(80, 72, 229, 0.12)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(80, 72, 229, 0.15)',
                        }
                      })
                    }}
                  >
                    <KeywordColumn>
                      <KeywordText>
                        {suggestion.keyword}
                        {isFirstItem && (
                          <Typography
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              color: '#5048E5',
                              backgroundColor: 'rgba(80, 72, 229, 0.1)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }}
                          >
                            Your keyword
                          </Typography>
                        )}
                      </KeywordText>
                    </KeywordColumn>
                    
                    <DifficultyColumn>
                      <DifficultyBadge 
                        difficulty={suggestion.keywordDifficulty || 0}
                        icon={<TargetIcon />}
                        label={suggestion.keywordDifficulty === null ? "N/A" : `${suggestion.keywordDifficulty}`}
                        size="small"
                      />
                    </DifficultyColumn>
                    
                    <VolumeColumn>
                      {hasNoData ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            padding: "4px 10px",
                            borderRadius: 2,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            border: '1px solid #d1d5db'
                          }}
                        >
                          <TrendingUpIcon sx={{ fontSize: 14 }} />
                          {formatNumber(suggestion.searchVolume)}
                        </Box>
                      ) : (
                        <VolumeBadge volume={suggestion.searchVolume || 0}>
                          <TrendingUpIcon sx={{ fontSize: 14 }} />
                          {formatNumber(suggestion.searchVolume)}
                        </VolumeBadge>
                      )}
                    </VolumeColumn>
                  </SuggestionItem>
                );
              })}
            </List>
          </Box>
        )}
      </SuggestionsContainer>
    </Popper>
  );
};

export default KeywordSuggestionsDropdown;